import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchQolAssessment } from "../../../store/slices/qolAssessmentsSlice";
import PrintWrapper, { printFooterStyles } from "../../common/PrintWrapper";
import logo from "../../../assets/images/logo-samplee.png";

export default function Qol2FormPrint() {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { currentQolAssessment, isLoading, error } = useSelector((state) => state.qolAssessments || {});

    useEffect(() => {
        if (id) dispatch(fetchQolAssessment(id));
    }, [dispatch, id]);

    useEffect(() => {
        if (currentQolAssessment) {
            const timer = setTimeout(() => window.print(), 1000);
            window.onafterprint = () => navigate(-1);
            return () => {
                clearTimeout(timer);
                window.onafterprint = null;
            };
        }
    }, [currentQolAssessment, navigate]);

    if (isLoading) return <div className="p-8 text-center">Loading QOLS Report...</div>;
    if (error) return <div className="p-8 text-danger text-center">{error}</div>;
    if (!currentQolAssessment) return <div className="p-8 text-center">No record found.</div>;

    const d = currentQolAssessment;

    // Mapping based on your exact backend response structure
    const qolItems = [
        { title: "Material comforts, financial security", scoreKey: "q1_material_score", valueKey: "q1_material_value" },
        { title: "Health - physically fit and vigorous", scoreKey: "q2_health_score", valueKey: "q2_health_value" },
        { title: "Relationships with relatives", scoreKey: "q3_relationship_score", valueKey: "q3_relationship_value" },
        { title: "Having and rearing children", scoreKey: "q4_having_score", valueKey: "q4_having_value" },
        { title: "Relationships with spouse/partner", scoreKey: "q5_spouse_score", valueKey: "q5_spouse_value" },
        { title: "Close friends", scoreKey: "q6_friends_score", valueKey: "q6_friends_value" },
        { title: "Helping/volunteering/advice", scoreKey: "q7_helping_score", valueKey: "q7_helping_value" },
        { title: "Participation in public affairs", scoreKey: "q8_participation_score", valueKey: "q8_participation_value" },
        { title: "Learning and knowledge", scoreKey: "q9_learning_score", valueKey: "q9_learning_value" },
        { title: "Self-understanding", scoreKey: "q10_selfunderstanding_score", valueKey: "q10_selfunderstanding_value" },
        { title: "Work - job or in home", scoreKey: "q11_work_score", valueKey: "q11_work_value" },
        { title: "Expressing yourself creatively", scoreKey: "q12_creativity_score", valueKey: "q12_creativity_value" },
        { title: "Socializing and parties", scoreKey: "q13_socializing_score", valueKey: "q13_socializing_value" },
        { title: "Reading/Music/Entertainment", scoreKey: "q14_entertainment_score", valueKey: "q14_entertainment_value" },
        { title: "Active recreation", scoreKey: "q15_recreation_score", valueKey: "q15_recreation_value" },
        { title: "Independence, doing for yourself", scoreKey: "q16_independence_score", valueKey: "q16_independence_value" }
    ];

    const totalScore = qolItems.reduce((sum, item) => sum + (parseInt(d[item.scoreKey] || 0, 10)), 0);

    return (
        <>
            <style jsx global>{`
                * { box-sizing: border-box !important; font-family: Arial, sans-serif !important; }
                body { background: #fff !important; color: #000 !important; font-size: 11px !important; margin: 0 !important; padding: 0 !important; }
                .print-container { padding: 30px !important; position: relative !important; }
                ${printFooterStyles}
                .logo { position: absolute !important; top: 15px !important; left: 15px !important; }
                .logo img { height: 60px !important; width: auto !important; }
                .header { text-align: center !important; margin-bottom: 20px !important; border-bottom: 2px solid #000 !important; padding-bottom: 10px !important; }
                .header h3 { font-size: 1.5rem !important; font-weight: bold !important; }
                .meta-table { width: 100% !important; border-collapse: collapse !important; margin-bottom: 15px !important; }
                .meta-table td { border: 1px solid #000 !important; padding: 6px !important; }
                .label-bg { background: #f2f2f2 !important; font-weight: bold !important; width: 18% !important; }
                .q-table { width: 100% !important; border-collapse: collapse !important; margin-top: 10px !important; }
                .q-table th, .q-table td { border: 1px solid #000 !important; padding: 5px 8px !important; }
                .q-table th { background: #eee !important; font-weight: bold !important; text-align: center !important; }
                .score-col { width: 50px !important; text-align: center !important; font-weight: bold !important; font-size: 13px !important; }
                .total-box { margin-top: 20px !important; border: 2px solid #000 !important; padding: 10px 20px !important; background: #f9f9f9 !important; display: flex !important; justify-content: space-between !important; align-items: center !important; }
                .total-text { font-size: 1.4rem !important; font-weight: bold !important; }
                .sig-row { display: flex !important; justify-content: space-between !important; margin-top: 50px !important; }
                .sig-box { border-top: 1px solid #000 !important; width: 220px !important; text-align: center !important; padding-top: 5px !important; font-weight: bold !important; }
                .footer { margin-top: 30px !important; border-top: 1px solid #000 !important; padding-top: 8px !important; text-align: center !important; font-size: 10px !important; }
                @media print { .q-table tr { page-break-inside: avoid !important; } }
            `}</style>

            <PrintWrapper>
                <div className="print-container">
                <div className="logo"><img src={logo} alt="Logo" /></div>
                <div className="header">
                    <h3>THE RECOVERY HOUSE</h3>
                    <h6>QUALITY OF LIFE SCALE (QOLS) REPORT</h6>
                </div>

                <table className="meta-table">
                    <tbody>
                        <tr>
                            <td className="label-bg">Client Name</td>
                            <td>{d.client_name}</td>
                            <td className="label-bg">Date</td>
                            <td>{new Date(d.created_at).toLocaleDateString('en-GB')}</td>
                        </tr>
                        <tr>
                            <td className="label-bg">Diagnosis</td>
                            <td>{d.diagnosis}</td>
                            <td className="label-bg">Assessed By</td>
                            <td>{d.assessed_by}</td>
                        </tr>
                    </tbody>
                </table>

                <table className="q-table">
                    <thead>
                        <tr>
                            <th style={{ width: '35px' }}>#</th>
                            <th>Quality of Life Item</th>
                            <th style={{ width: '150px' }}>Satisfaction Level</th>
                            <th className="score-col">Score</th>
                        </tr>
                    </thead>
                    <tbody>
                        {qolItems.map((item, index) => (
                            <tr key={index}>
                                <td style={{ textAlign: 'center' }}>{index + 1}</td>
                                <td style={{ fontWeight: '500' }}>{item.title}</td>
                                <td style={{ textAlign: 'center' }}>{d[item.valueKey]}</td>
                                <td className="score-col">{d[item.scoreKey]}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* <div className="total-box">
                    <div>
                        <div className="total-text">AGGREGATE QOL SCORE: {totalScore}</div>
                        <div style={{ fontSize: '10px', color: '#555' }}>Based on 16 items (Flanagan revised version)</div>
                    </div>
                    <div style={{ textAlign: 'right', fontSize: '10px' }}>
                        Scale: 1 (Terrible) to 7 (Delighted)
                    </div>
                </div> */}

                <div className="sig-row">
                    <div className="sig-box">Patient Signature</div>
                    <div className="sig-box">Clinician Signature</div>
                </div>

                <div className="footer">
                    <p>156 Bangalore Town, Tipu Sultan Road Karachi-Pakistan | Ph: 021-34546364-65-66</p>
                    <p><strong>A Project of “CARVAN OF LIFE PAKISTAN TRUST”</strong></p>
                </div>
            </div>
            </PrintWrapper>
        </>
    );
}